/* eslint-disable @typescript-eslint/no-this-alias */
/* eslint-disable no-undef */
/**
 * ractiveModal(contentTemplate, [ractiveOptions]) -> EventEmitter
 *
 * A modal widget. The modal will be dismissed if the Esc key is pressed.
 *
 * Arguments:
 * - contentTemplate: A Ractive template of the content that the modal will
 *   display. This content will be contained in a div [name "modal-content"].
 * - ractiveOptions: Any options to pass on to the Ractive constructor. Set
 *   the `template` property of this object to replace the built-in default
 *   template.
 *
 * Events:
 * - 'open'
 *    Emitted when modal is opened.
 * - 'close'
 *    Emitted when modal is closed.
 * - 'reposition' (newPosition)
 *    Emitted when modal is repositioned.
 *    `newPosition`: Object with keys `top` and `left`.
 *
 * Properties:
 * - modalContent (element): the [name="modal-content"] element that contains
 *   the modal content.
 * - el (element): The root element of the modal.
 * - view (Ractive): The Ractive instance.
 *
 * Methods:
 * - open(): Opens the modal & emits 'open'
 * - close(): Closes the modal & emits 'close'
 */

const EE = require('events')

const extend = require('xtend')

const Ractive = require('../../modules/ractive')
const template = require('../../templates/widgets/ractive-modal.ract')
const emptyTemplate = require('airship/templates/empty-template.ract')

module.exports = createRactiveModal

function createRactiveModal(...args) {
  return new RactiveModal(...args)
}

function RactiveModal(
  contentTemplate = emptyTemplate,
  ractiveOptions = {},
  {className = ''} = {}
) {
  EE.call(this)

  const el = document.body.appendChild(document.createElement('div'))

  if (className) {
    el.classList.add(className)
  }

  ractiveOptions = extend(
    {
      el: el,
      template: template,
      data: {
        state: {
          isOpen: false,
          isFixed: false,
        },
      },
    },
    ractiveOptions
  )

  ractiveOptions.partials = extend(ractiveOptions.partials, {
    contentTemplate: contentTemplate,
  })

  this.view = new Ractive(ractiveOptions)
  this.el = this.view.el
  this.modalContent = el.querySelector('[name=modal-content]')
  this.window = $(window)
  this.keyUpListener = keyUpListener.bind(this)
  this.windowResizeListener = windowResizeListener.bind(this)
  this.addListeners()
}

RactiveModal.prototype = Object.create(EE.prototype)

RactiveModal.prototype.addListeners = function() {
  const self = this

  self.view.observe('state.isOpen', isOpen => {
    self.isOpen = isOpen
  })

  self.view.on('close', function(rev) {
    rev.original.stopPropagation()
    rev.original.preventDefault()

    self.close()
  })

  document.body.addEventListener('keyup', self.keyUpListener)
  window.addEventListener('resize', self.windowResizeListener)
}

RactiveModal.prototype.calculateOffset = function() {
  const content = $(this.el).find('[name=modal-content]'),
    win = this.window,
    margin = 0.9

  const height = Math.min(content.outerHeight(), win.height() * margin),
    width = Math.min(content.outerWidth(), win.width() * margin)

  let top = Math.min((win.height() - height) / 2, win.height() * margin),
    left = Math.min((win.width() - width) / 2, win.width() * margin)

  // If using fixed positioning, calculate offset from viewport
  // size, which is how fixed positioning works.
  // Otherwise, calculate offset from document scroll position.
  if (!this.view.get('state.isFixed')) {
    left += win.scrollLeft()
    top += win.scrollTop()
  }

  return {top: top, left: left}
}

RactiveModal.prototype.reposition = function() {
  const {top, left} = this.calculateOffset()

  $(this.el)
    .find('[name=modal-window]')
    .css({
      left: left + 'px',
      top: top + 'px',
    })

  this.emit('reposition', {top, left})
}

RactiveModal.prototype.open = function() {
  this.view.set('state.isOpen', true)
  this.reposition()
  this.emit('open')
}

RactiveModal.prototype.close = function() {
  this.view.set('state.isOpen', false)
  this.emit('close')
}

RactiveModal.prototype.setFixed = function(fixed) {
  this.view.set('state.isFixed', fixed)
}

RactiveModal.prototype.teardown = function() {
  document.body.removeEventListener('keyup', this.keyUpListener)
  window.removeEventListener('resize', this.windowResizeListener)
  this.view.teardown()
  document.body.removeChild(this.el)
}

// will be bound to RactiveModal instance
function keyUpListener(ev) {
  if (ev.keyCode === 27) {
    this.close()
  }
}

function windowResizeListener() {
  if (this.isOpen) {
    this.reposition()
  }
}
